import React, { useState } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle'
import clsx from 'clsx'

import { Card } from '@infura/packages.ui/Card'
import { Heading } from '@infura/packages.ui/Heading'
import { Text } from '@infura/packages.ui/Text'

import { CardInfo } from 'components/billing/CardInfo'
import ChargifyCCForm from 'components/billing/ChargifyCCForm'
import { useCheckoutContext } from 'components/checkout/Context'

import useCard from 'hooks/useCard'

export const PAYMENT_DETAILS_TITLE_DATA_TEST_ID = 'payment-details-title'

type Props = {
  isPaymentFormVisible: boolean
  setIsPaymentFormVisible: (isVisible: boolean) => void
}

export default function PaymentDetails({ isPaymentFormVisible, setIsPaymentFormVisible }: Props) {
  const { checkoutCloseCallback, revalidateUser, showPaymentFormCancelButton } = useCheckoutContext()
  const [chargifyError, setChargifyError] = useState('')
  const { card: chargifyCard, loading } = useCard()
  const hasPaymentMethod = !!chargifyCard
  const handleCancelClick = () => {
    hasPaymentMethod ? setIsPaymentFormVisible(false) : checkoutCloseCallback?.()
  }

  const paymentForm = (
    <ChargifyCCForm
      ctaText={!!chargifyCard ? 'Update Billing Info' : 'Add Billing Info'}
      formBackground='default'
      onCancel={showPaymentFormCancelButton ? handleCancelClick : undefined}
      onError={e => {
        setChargifyError(e)
      }}
      onSuccess={() => {
        revalidateUser()
        setIsPaymentFormVisible(false)
      }}
      showErrorBanner={false}
      showHeadings={false}
    />
  )

  return (
    <Card className={'!p-0'}>
      <div
        className={clsx(
          'border-border-muted flex flex-wrap items-center justify-center gap-2 border-b px-6 py-4 sm:flex-nowrap'
        )}
      >
        <Heading data-testid={PAYMENT_DETAILS_TITLE_DATA_TEST_ID} theme='section'>
          Payment Details
        </Heading>
        {chargifyError ? (
          <div className='flex items-center text-red-500'>
            <FontAwesomeIcon className='mr-2' icon={faExclamationTriangle} />
            <Text as='span' theme='sm'>
              {chargifyError}
            </Text>
          </div>
        ) : null}
      </div>
      <div className='p-6 text-left'>
        {loading || (hasPaymentMethod && !isPaymentFormVisible) ? (
          <CardInfo
            creditCard={chargifyCard}
            loading={loading}
            togglePaymentForm={() => setIsPaymentFormVisible(true)}
          />
        ) : (
          paymentForm
        )}
      </div>
    </Card>
  )
}
